import { useState } from "react";
import styles from "./Auth.module.css";
import { loginUser, getUserRole } from "../../lib/directus";

function DriftWoodLogo() {
  return (
    <div className="mb-2">
      <img
        src="/driftwood-logo.png"
        alt="Driftwood Agri logo"
        className={styles.logoImage}
      />
    </div>
  );
}

function EyeIcon({ visible }) {
  return visible ? (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <img
      src="/check-mark.svg"
      alt="Check icon"
      className={`${styles.successIcon} mb-2`}
    />
  );
}

function PasswordInput({ value, onChange, placeholder }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className={`position-relative ${styles.passwordWrapper}`}>
      <input
        type={visible ? "text" : "password"}
        className={styles.input}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
      />
      <button
        type="button"
        className={`d-flex align-items-center ${styles.eyeButton}`}
        onClick={() => setVisible((v) => !v)}
        tabIndex={-1}
      >
        <EyeIcon visible={visible} />
      </button>
    </div>
  );
}

/* ── Views ── */

function LoginView({ onForgotPassword, onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setError("");
    setLoading(true);
    try {
      const result = await loginUser(email, password);
      console.log("Login response:", result);
      console.log("User role:", getUserRole());
      onLogin();
    } catch (err) {
      setError(err?.errors?.[0]?.message || "Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.card}>
      <DriftWoodLogo />
      <h1 className={`${styles.heading} mb-4`}>Log into your account</h1>

      <div className="mb-3">
        <label className={`form-label ${styles.label}`}>Email</label>
        <input
          type="email"
          className={styles.input}
          placeholder="johndoe@gmail.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      <div className="mb-3">
        <label className={`form-label ${styles.label}`}>Password</label>
        <PasswordInput
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="****************"
        />
      </div>

      {error && (
        <p className="text-danger mb-2" style={{ fontSize: 13 }}>{error}</p>
      )}

      <button
        type="button"
        className={`w-100 mt-2 ${styles.btnPrimary}`}
        onClick={handleLogin}
        disabled={loading}
      >
        {loading ? "Logging in…" : "Login"}
      </button>

      <button
        type="button"
        className={`w-100 text-center mt-3 ${styles.linkText}`}
        onClick={onForgotPassword}
      >
        Forgot password?
      </button>
    </div>
  );
}

function ForgotPasswordView({ onGoBack, onSubmit }) {
  const [email, setEmail] = useState("");

  return (
    <div className={styles.card}>
      <DriftWoodLogo />
      <h1 className={`${styles.heading} mb-4`}>Enter&nbsp; account email</h1>

      <div className="mb-3">
        <label className={`form-label ${styles.label}`}>Email</label>
        <input
          type="email"
          className={styles.input}
          placeholder="johndoe@gmail.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      <button
        type="button"
        className={`w-100 mt-2 ${styles.btnPrimary}`}
        onClick={onSubmit}
      >
        Submit
      </button>

      <button
        type="button"
        className={`w-100 text-center mt-3 ${styles.linkText}`}
        onClick={onGoBack}
      >
        Go back
      </button>
    </div>
  );
}

function ResetPasswordView({ onReset }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  return (
    <div className={styles.card}>
      <DriftWoodLogo />
      <h1 className={`${styles.heading} mb-4`}>Reset password</h1>

      <div className="mb-3">
        <label className={`form-label ${styles.label}`}>Password</label>
        <PasswordInput
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="****************"
        />
      </div>

      <div className="mb-3">
        <label className={`form-label ${styles.label}`}>Confirm password</label>
        <PasswordInput
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="****************"
        />
      </div>

      <button
        type="button"
        className={`w-100 mt-2 ${styles.btnPrimary}`}
        onClick={onReset}
      >
        Reset password
      </button>
    </div>
  );
}

function ResetSuccessView({ onGoToLogin }) {
  return (
    <div
      className={`${styles.card} d-flex flex-column align-items-center text-center`}
    >
      <CheckIcon />
      <p className={`${styles.successTitle} mb-3 mt-2`}>
        Password reset successful
      </p>
      <button
        type="button"
        className={styles.successLink}
        onClick={onGoToLogin}
      >
        Go to login page
      </button>
    </div>
  );
}

/* ── Main Auth Component ── */

const VIEWS = {
  LOGIN: "login",
  FORGOT: "forgot",
  RESET: "reset",
  SUCCESS: "success",
};

export default function Auth({ onLogin }) {
  const [view, setView] = useState(VIEWS.LOGIN);

  return (
    <div
      className={`${styles.pageWrapper} d-flex align-items-center justify-content-center min-vh-100`}
    >
      <div className="container">
        <div className="d-flex align-items-center justify-content-center">
          {view === VIEWS.LOGIN && (
            <LoginView onForgotPassword={() => setView(VIEWS.FORGOT)} onLogin={onLogin} />
          )}
          {view === VIEWS.FORGOT && (
            <ForgotPasswordView
              onGoBack={() => setView(VIEWS.LOGIN)}
              onSubmit={() => setView(VIEWS.RESET)}
            />
          )}
          {view === VIEWS.RESET && (
            <ResetPasswordView onReset={() => setView(VIEWS.SUCCESS)} />
          )}
          {view === VIEWS.SUCCESS && (
            <ResetSuccessView onGoToLogin={() => setView(VIEWS.LOGIN)} />
          )}
        </div>
      </div>
    </div>
  );
}
